// Tiny app-wide toast store — no context/provider needed (useSyncExternalStore).
// Use it to surface action results (esp. errors that were previously swallowed):
//   import { toastError, toastSuccess } from "@/lib/toast";
//   catch (e) { toastError(e.message); }
// A single <Toaster /> mounted at the app root renders the stack.
import { useSyncExternalStore } from "react";

// How many toasts may be on screen at once.
const MAX_TOASTS = 4;

let toasts = [];
const listeners = new Set();
let counter = 0;

function emit() {
  listeners.forEach((l) => l());
}

export function toast(type, message, ttl = 5000) {
  const text = typeof message === "string" ? message : (message?.message || "");
  if (!text) return null;

  // A REPEAT REPLACES ITSELF rather than stacking.
  //
  // Every call used to push a new pill, so pressing one button five times —
  // Refresh on the activity log is the obvious case — left five identical
  // "refreshed" messages piled up, one per click, each with its own 5s timer.
  // The second confirmation of the same thing carries no information; it just
  // buries the page. An existing toast with the same type AND text is reused:
  // it stays where it is, and its timer restarts so it lives a full ttl from
  // the LATEST action. Different messages still stack as before.
  const existing = toasts.find((t) => t.type === type && t.message === text);
  if (existing) {
    if (existing.timer) clearTimeout(existing.timer);
    existing.timer = ttl ? setTimeout(() => dismiss(existing.id), ttl) : null;
    // A new array identity so useSyncExternalStore sees a change and the pill
    // re-renders (it may have been mid-fade).
    toasts = [...toasts];
    emit();
    return existing.id;
  }

  const id = ++counter;
  // Capped. Unbounded, a loop of failures — a bulk mark-read, a row of report
  // buttons — filled the viewport with pills and buried the page under them.
  // Oldest fall off; each still expires on its own timer.
  const entry = { id, type, message: text, timer: null };
  toasts = [...toasts, entry].slice(-MAX_TOASTS);
  emit();
  if (ttl) entry.timer = setTimeout(() => dismiss(id), ttl);
  return id;
}

export function dismiss(id) {
  const going = toasts.find((t) => t.id === id);
  if (going && going.timer) clearTimeout(going.timer);
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export const toastError = (message) => toast("error", message || "Something went wrong. Please try again.");
export const toastSuccess = (message) => toast("success", message);
export const toastInfo = (message) => toast("info", message);

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot() {
  return toasts;
}

export function useToasts() {
  return useSyncExternalStore(subscribe, getSnapshot);
}
